import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../i18n';
import { api } from '../lib/api';
import { useAgentStatus, useIngestions } from '../lib/queries';
import { fmtDateTime, fmtRelative } from '../lib/format';
import type { AgentStatus, IngestionStatus, MorningStatus } from '../lib/types';
import { Button, Callout, Confidence, Dialog, Field, IngestionBadge, Input, Ltr, Pagination, Select, SkeletonRows, Empty, useConfirm, useToast } from '../components/ui';
import { agentTone, useOpenDocument } from '../components/Layout';
import { IconExternal, IconMail, IconRefresh } from '../components/icons';
import { useReaderStatus } from '../components/ReaderSettings';
import { useCan } from '../lib/auth';

export function AutomationPage() {
  const { data: s, refetch } = useAgentStatus(true);
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [backfill, setBackfill] = useState(false);

  // Result of the Google OAuth redirect.
  useEffect(() => {
    const g = params.get('gmail');
    if (!g) return;
    if (g === 'connected') toast.success('תיבת הדואר חוברה בהצלחה. הסוכן מתחיל לבדוק הודעות.');
    else toast.error(`החיבור נכשל: ${params.get('reason') ?? ''}`);
    const next = new URLSearchParams(params);
    next.delete('gmail');
    next.delete('reason');
    setParams(next, { replace: true });
  }, [params, setParams, toast]);

  const syncNow = async () => {
    try {
      const r = await api<{ jobId: number; agentAlive?: boolean; alreadyQueued?: boolean }>('/api/agent/sync', { method: 'POST' });
      if (r.agentAlive === false) toast.error('הבקשה נשמרה, אבל הסוכן אינו פועל כרגע.');
      else toast.success(r.alreadyQueued ? 'בדיקה כבר בתור' : 'הבדיקה התחילה');
      void refetch();
    } catch (e) {
      toast.error(e);
    }
  };

  const disconnect = async () => {
    if (!(await confirm({ title: t.agent.disconnectConfirm, danger: true, confirmLabel: t.agent.disconnect }))) return;
    try {
      await api('/api/integrations/gmail', { method: 'DELETE' });
      qc.invalidateQueries({ queryKey: ['agent-status'] });
      toast.success('תיבת הדואר נותקה');
    } catch (e) {
      toast.error(e);
    }
  };

  const tone = agentTone(s);
  const running = s?.jobs.find((j) => j.status === 'running' || j.status === 'queued');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.agent.title}</h1>
          <div className="sub">{t.agent.sub}</div>
        </div>
        <div className="row">
          <Link to="/settings" className="btn">{t.agent.rules}</Link>
          <Button onClick={() => setBackfill(true)} disabled={s?.account?.status !== 'connected'}>{t.agent.backfill}</Button>
          <Button variant="primary" onClick={syncNow} disabled={s?.overall === 'setup_required' || s?.overall === 'not_connected' || s?.overall === 'auth_error'}>
            <IconRefresh /> {t.agent.syncNow}
          </Button>
        </div>
      </div>

      <div className="stack">
        <section className="panel">
          <div className="panel-head">
            <span className={`status-line ${tone.tone}`} style={{ fontSize: 15 }}>
              <span className="dot" style={{ width: 9, height: 9 }} /> {tone.label}
            </span>
            <span className="spacer" />
            {s?.agent.currentActivity ? (
              <span className="row small muted"><span className="spinner" /> {s.agent.currentActivity}</span>
            ) : null}
          </div>
          <div className="panel-body stack">
            {s ? <ConnectionState s={s} onDisconnect={disconnect} /> : <span className="skeleton" style={{ width: '40%' }} />}
            {running ? (
              <Callout tone="info" title={running.type === 'sync' ? 'בדיקה בתהליך' : t.agent.backfillRunning}>
                {running.progress?.total != null ? `נסרקו ${running.progress.scanned ?? 0} מתוך ${running.progress.total} מיילים · יובאו ${running.progress.imported ?? 0} מסמכים` : 'ממתין לסוכן…'}
              </Callout>
            ) : s?.lastJob?.status === 'failed' ? (
              <Callout tone="bad" title="המשימה האחרונה נכשלה">{s.lastJob.error}</Callout>
            ) : null}
          </div>
          {s ? (
            <div className="kpis" style={{ border: 0, borderTop: '1px solid var(--line)', borderRadius: 0 }}>
              <Stat label={t.agent.lastChecked} value={s.agent.lastCheckedAt ? fmtRelative(s.agent.lastCheckedAt) : '—'} meta={s.agent.lastCheckedAt ? fmtDateTime(s.agent.lastCheckedAt) : undefined} />
              <Stat label={t.agent.lastDetected} value={s.agent.lastDetectedAt ? fmtRelative(s.agent.lastDetectedAt) : '—'} />
              <Stat label={t.agent.lastImported} value={s.agent.lastImportedAt ? fmtRelative(s.agent.lastImportedAt) : '—'} />
              <Stat label={t.agent.processed} value={s.counters.processed} />
              <Stat label={t.agent.matched} value={s.counters.matched + s.counters.resolved} meta={s.counters.resolved ? `${s.counters.resolved} מהם ידנית` : undefined} />
              <Stat label={t.agent.needsReview} value={s.counters.needsReview} to={s.counters.needsReview ? '/review' : undefined} tone={s.counters.needsReview ? 'warn' : undefined} />
              <Stat label={t.agent.errors} value={s.counters.failed} tone={s.counters.failed ? 'bad' : undefined} />
            </div>
          ) : null}
        </section>

        <MorningPanel />
        <ReaderPanel />

        <ProcessingLog />
      </div>

      {backfill ? <BackfillDialog onClose={() => setBackfill(false)} /> : null}
    </>
  );
}

/** Morning agent: the primary source. The mailbox above is its cross-check. */
/** The reading agent (product lists → quotes): connection, this month's use, last reads. */
function ReaderPanel() {
  const status = useReaderStatus();
  const can = useCan();
  const runs = useQuery({ queryKey: ['reader', 'runs'], queryFn: () => api<{ runs: Array<{ id: number; customer_id: number; business_name: string; input_kind: string; status: string; engine: string; lines: number; error: string | null; order_id: number | null; created_at: string }>; learned: number }>('/api/reader/runs'), enabled: can('orders.edit') });
  const s = status.data;
  if (!s) return null;
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>סוכן קריאה — רשימות מוצרים</h2>
        <span className="spacer" />
        {s.connected ? (s.lastTestOk === false ? <span className="badge bad">החיבור נכשל</span> : <span className="badge ok">מחובר</span>) : <span className="badge warn">לא מחובר</span>}
        <Link to="/settings?tab=business" className="btn sm">{s.connected ? 'הגדרות' : 'חיבור'}</Link>
      </div>
      <div className="panel-body stack" style={{ gap: 8 }}>
        <p className="small muted" style={{ margin: 0 }}>
          בתיק הלקוח ← "הצעת מחיר מרשימה": מדביקים תמונה, PDF או טקסט של רשימת מוצרים, הסוכן מתאים כל שורה לקטלוג במחיר של הלקוח ופותח טיוטה.
          {s.connected ? ` החודש: ${s.usage.reads} קריאות מתוך ${s.monthlyLimit}.` : ' בלי חיבור — רק טקסט מודבק, בהתאמה בסיסית.'}
          {runs.data ? ` ${runs.data.learned} ניסוחים נלמדו.` : ''}
        </p>
        {runs.data?.runs.length ? (
          <table className="table">
            <tbody>
              {runs.data.runs.slice(0, 8).map((r) => (
                <tr key={r.id}>
                  <td><Link to={`/customers/${r.customer_id}`} style={{ color: 'var(--ink)' }}>{r.business_name}</Link></td>
                  <td className="small muted">{r.input_kind === 'image' ? 'תמונה' : r.input_kind === 'pdf' ? 'PDF' : 'טקסט'} · {r.lines} שורות</td>
                  <td className="small">{r.status === 'failed' ? <span style={{ color: 'var(--bad)' }}>{r.error}</span> : r.order_id ? <Link to={`/orders/${r.order_id}`}>טיוטה</Link> : <span className="muted">נקרא</span>}</td>
                  <td className="num small muted">{fmtDateTime(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </section>
  );
}

function MorningPanel() {
  const { data: m, refetch } = useQuery({ queryKey: ['morning'], queryFn: () => api<MorningStatus>('/api/integrations/morning'), refetchInterval: 15_000 });
  const toast = useToast();
  const open = useOpenDocument();
  const syncNow = async () => {
    try {
      const r = await api<{ alreadyQueued?: boolean }>('/api/integrations/morning/sync', { body: { full: true } });
      toast.success(r.alreadyQueued ? 'סנכרון כבר בתור' : 'הסנכרון ממורנינג התחיל');
      void refetch();
    } catch (e) {
      toast.error(e);
    }
  };
  if (!m) return null;
  if (!m.connected) {
    return (
      <section className="panel">
        <div className="panel-head"><h2>סוכן מורנינג</h2></div>
        <div className="panel-body">
          <Callout tone="info" title="מורנינג לא מחובר" action={<Link to="/settings?tab=business" className="btn sm">חיבור</Link>}>
            כשמורנינג מחובר, הסוכן מושך את המסמכים ישירות ממורנינג (המקור הראשי) והמייל משמש לאימות.
          </Callout>
        </div>
      </section>
    );
  }
  const v = m.verification;
  const st = m.sync?.lastStats;
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>סוכן מורנינג — המקור הראשי</h2>
        <span className={`badge ${m.sync?.lastError ? 'warn' : m.sync?.lastSuccessAt ? 'ok' : ''}`}>{m.sync?.lastError ? 'דורש תשומת לב' : m.sync?.lastSuccessAt ? 'פעיל' : 'ממתין לסנכרון ראשון'}</span>
        <span className="spacer" />
        <span className="small muted">{m.sync?.lastSuccessAt ? `סונכרן ${fmtRelative(m.sync.lastSuccessAt)}` : ''}</span>
        <Button size="sm" onClick={syncNow}><IconRefresh /> סנכרון מלא עכשיו</Button>
      </div>
      <div className="panel-body stack">
        <p className="small muted">
          בכל בדיקה הסוכן מושך ממורנינג את המסמכים החדשים (ופעם בשעה את כל החשבון): מסמך שכבר קיים מקושר ונבדק מול הסכום, מסמך חדש נקרא ומשויך ללקוח.
          חשבונית מס שנסגרה במורנינג — גם ידנית, בלי קבלה — מסומנת כשולמה; אם נפתחה מחדש במורנינג, הסימון מוסר. המייל ממשיך לפעול ומאמת כל מסמך.
        </p>
        {m.sync?.lastError ? <Callout tone="warn">{m.sync.lastError}</Callout> : null}
        {v && v.mismatches.length ? (
          <Callout tone="bad" title={`${v.mismatches.length} מסמכים שהסכום שלהם במערכת שונה ממורנינג`}>
            <div className="row wrap" style={{ gap: 6 }}>
              {v.mismatches.slice(0, 12).map((x) => (
                <button key={x.documentId} className="badge outline" onClick={() => open(x.documentId)}>
                  {x.number}: {((x.ours ?? 0) / 100).toFixed(2)} ≠ {(x.morning / 100).toFixed(2)}
                </button>
              ))}
            </div>
          </Callout>
        ) : null}
      </div>
      {v ? (
        <div className="kpis" style={{ border: 0, borderTop: '1px solid var(--line)', borderRadius: 0 }}>
          <Stat label="מסמכים ממורנינג" value={v.fromMorning} meta={v.since ? `מאז ${v.since.split('-').reverse().join('/')}` : undefined} />
          <Stat label="אומתו גם במייל" value={v.verifiedByEmail} meta={v.fromMorning ? `${Math.round((v.verifiedByEmail / v.fromMorning) * 100)}%` : undefined} />
          <Stat label="רק במורנינג (טרם הגיעו במייל)" value={v.morningOnly} />
          <Stat label="במייל ולא במורנינג" value={v.notInMorning} tone={v.notInMorning ? 'warn' : undefined} />
          <Stat label="נסגרו במורנינג → שולם" value={v.markedPaidByMorning} />
          <Stat label="הפרשי סכום" value={v.mismatches.length} tone={v.mismatches.length ? 'bad' : undefined} />
          {st ? <Stat label="בבדיקה האחרונה" value={st.seen} meta={`חדשים ${st.imported ?? 0} · לבדיקה ${st.review ?? 0} · נכשלו ${st.failed ?? 0}`} /> : null}
        </div>
      ) : null}
    </section>
  );
}

function Stat({ label, value, meta, tone, to }: { label: string; value: React.ReactNode; meta?: string; tone?: 'warn' | 'bad'; to?: string }) {
  const inner = (
    <>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={{ fontSize: 17, ...(tone ? { color: `var(--${tone})` } : {}) }}>{value}</div>
      {meta ? <div className="kpi-meta">{meta}</div> : null}
    </>
  );
  return to ? <Link to={to} className="kpi" style={{ color: 'inherit', textDecoration: 'none' }}>{inner}</Link> : <div className="kpi">{inner}</div>;
}

function ConnectionState({ s, onDisconnect }: { s: AgentStatus; onDisconnect: () => void }) {
  if (!s.gmailConfigured) {
    return (
      <Callout tone="warn" title="מה צריך כדי להפעיל את הסוכן">
        <p>כדי לחבר את Gmail צריך להגדיר בשרת פרטי OAuth של Google (משתני סביבה <Ltr>GOOGLE_CLIENT_ID</Ltr>, <Ltr>GOOGLE_CLIENT_SECRET</Ltr>, <Ltr>GOOGLE_REDIRECT_URI</Ltr>). ההנחיות המלאות נמצאות בקובץ README של הפרויקט.</p>
        <p className="small muted" style={{ marginTop: 6 }}>המערכת מבקשת הרשאת קריאה בלבד — היא לעולם לא שולחת, מוחקת או משנה מיילים.</p>
      </Callout>
    );
  }
  if (!s.account) {
    return (
      <div className="row wrap" style={{ gap: 16 }}>
        <div style={{ flex: '1 1 320px' }}>
          <div className="strong">{t.agent.status.not_connected}</div>
          <p className="muted small">חברו את תיבת הדואר שאליה מורנינג שולח את המסמכים. ההרשאה היא לקריאה בלבד.</p>
        </div>
        <a className="btn primary" href="/api/integrations/gmail/connect">
          <IconMail /> {t.agent.connect}
        </a>
      </div>
    );
  }
  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row wrap" style={{ gap: 16 }}>
        <div style={{ flex: '1 1 320px' }}>
          <div className="row" style={{ gap: 8 }}>
            <IconMail />
            <bdi className="strong">{s.account.emailAddress}</bdi>
            {s.account.status === 'connected' ? <span className="badge ok">מחובר</span> : <span className="badge bad">נדרש חיבור מחדש</span>}
          </div>
          <div className="small muted" style={{ marginTop: 2 }}>
            חובר {fmtDateTime(s.account.connectedAt)} · סנכרון אחרון {s.account.lastSyncedAt ? fmtRelative(s.account.lastSyncedAt) : 'טרם בוצע'}
          </div>
        </div>
        {s.account.status !== 'connected' ? (
          <a className="btn primary" href="/api/integrations/gmail/connect">{t.agent.reconnect}</a>
        ) : null}
        <Button variant="danger" size="sm" onClick={onDisconnect}>{t.agent.disconnect}</Button>
      </div>
      {s.account.lastError ? <Callout tone="bad">{s.account.lastError}</Callout> : null}
      {s.overall === 'agent_offline' ? (
        <Callout tone="bad" title={t.agent.status.agent_offline}>
          {s.agent.heartbeatAt ? `סימן חיים אחרון: ${fmtDateTime(s.agent.heartbeatAt)}. ` : ''}
          {t.agent.offlineHelp}
        </Callout>
      ) : null}
      {s.overall === 'error' && s.agent.lastError ? (
        <Callout tone="warn" title="שגיאה בבדיקה האחרונה">
          <span className="small">{s.agent.lastError}</span>
          <div className="small muted">{fmtDateTime(s.agent.lastErrorAt)}</div>
        </Callout>
      ) : null}
    </div>
  );
}

const LOG_FILTERS: Array<{ key: string; label: string }> = [
  { key: '', label: 'כל המסמכים' },
  { key: 'matched,processed', label: 'שויכו' },
  { key: 'needs_review', label: 'ממתינים' },
  { key: 'failed', label: 'נכשלו' },
  { key: 'duplicate', label: 'כפולים' },
  { key: 'ignored', label: 'סוננו' },
];

function ProcessingLog() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading } = useIngestions({ status: status || undefined, page, pageSize: 50 });
  const toast = useToast();
  const qc = useQueryClient();

  const retry = async (id: number) => {
    try {
      await api(`/api/ingestions/${id}/retry`, { method: 'POST' });
      qc.invalidateQueries({ queryKey: ['ingestions'] });
      qc.invalidateQueries({ queryKey: ['agent-status'] });
      toast.success('המסמך עובד מחדש');
    } catch (e) {
      toast.error(e);
    }
  };

  const count = (keys: string) => keys.split(',').reduce((s, k) => s + (data?.counts[k] ?? 0), 0);

  return (
    <section className="panel">
      <div className="panel-head row wrap">
        <h2>{t.agent.log}</h2>
        <span className="spacer" />
        <div className="segmented" role="group">
          {LOG_FILTERS.map((f) => (
            <button
              key={f.key}
              className={status === f.key ? 'active' : ''}
              onClick={() => {
                setStatus(f.key);
                setPage(1);
              }}
            >
              {f.label}
              {f.key && data ? <span className="faint tabular"> {count(f.key)}</span> : null}
            </button>
          ))}
        </div>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>התקבל</th>
              <th className="hide-sm">שולח / נושא</th>
              <th>מסמך</th>
              <th>לקוח שזוהה</th>
              <th>ביטחון</th>
              <th>סטטוס</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows cols={7} />
            ) : data && data.items.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ height: 'auto' }}>
                  <Empty title="אין רשומות" body={status ? 'אין מסמכים בסטטוס זה.' : 'מסמכים שיתקבלו במייל או יועלו ידנית יירשמו כאן.'} />
                </td>
              </tr>
            ) : (
              data?.items.map((i) => (
                <tr key={i.id}>
                  <td className="nowrap tabular small">{fmtDateTime(i.receivedAt ?? i.createdAt)}</td>
                  <td className="hide-sm" style={{ maxWidth: 280 }}>
                    <div className="ellipsis small">{i.source === 'manual' ? 'העלאה ידנית' : <bdi>{i.sender?.replace(/<.*>/, '').replace(/"/g, '').trim() || i.sender}</bdi>}</div>
                    <div className="ellipsis small muted" title={i.subject ?? ''}>{i.subject ?? i.attachmentName}</div>
                  </td>
                  <td className="nowrap">
                    {i.detectedTypeLabel ? <>{i.detectedTypeLabel} <Ltr className="tabular">{i.detectedNumber}</Ltr></> : <span className="faint">—</span>}
                  </td>
                  <td style={{ maxWidth: 220 }}>
                    {i.matchedCustomerId && (i.status === 'matched' || i.status === 'processed') ? (
                      <Link to={`/customers/${i.matchedCustomerId}`} className="ellipsis" style={{ display: 'block' }}>{i.matchedCustomerName}</Link>
                    ) : i.extracted?.customerName ? (
                      <span className="muted ellipsis" style={{ display: 'block' }}>{i.extracted.customerName}</span>
                    ) : (
                      <span className="faint">—</span>
                    )}
                    {i.errorMessage && i.status !== 'matched' ? <span className="sub" title={i.errorMessage}>{i.errorMessage}</span> : null}
                  </td>
                  <td>
                    {i.status === 'processed' ? (
                      <span className="small muted">ידני</span>
                    ) : (
                      <Confidence value={i.status === 'ignored' || i.status === 'failed' || i.status === 'duplicate' ? null : i.confidence} />
                    )}
                  </td>
                  <td><IngestionBadge status={i.status as IngestionStatus} /></td>
                  <td className="num nowrap">
                    {i.documentId ? <Link to={`?doc=${i.documentId}`} className="btn sm">פתיחה</Link> : null}
                    {i.status === 'needs_review' ? <Link to={`/review?item=${i.id}`} className="btn sm">בדיקה</Link> : null}
                    {i.status === 'duplicate' && i.duplicateOfDocumentId ? <Link to={`?doc=${i.duplicateOfDocumentId}`} className="btn sm ghost">המסמך הקיים</Link> : null}
                    {i.status === 'failed' || i.status === 'ignored' ? <Button size="sm" variant="ghost" onClick={() => retry(i.id)}>עיבוד מחדש</Button> : null}
                    {i.gmailUrl ? (
                      <a className="btn sm ghost icon" href={i.gmailUrl} target="_blank" rel="noreferrer noopener" title={t.docs.openEmail} aria-label={t.docs.openEmail}>
                        <IconExternal />
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {data && data.total > data.pageSize ? <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} /> : null}
    </section>
  );
}

function BackfillDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const d = new Date();
  d.setMonth(d.getMonth() - 12);
  const [since, setSince] = useState(d.toISOString().slice(0, 10));
  const [preset, setPreset] = useState('12');
  const [busy, setBusy] = useState(false);
  const setMonths = (m: string) => {
    setPreset(m);
    if (m === 'custom') return;
    const x = new Date();
    x.setMonth(x.getMonth() - Number(m));
    setSince(x.toISOString().slice(0, 10));
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={t.agent.backfill}
      footer={
        <>
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await api<{ agentAlive: boolean }>('/api/agent/backfill', { body: { since } });
                toast.success(r.agentAlive ? 'הייבוא התחיל. ניתן להמשיך לעבוד בינתיים.' : 'המשימה נשמרה ותתבצע כשהסוכן יפעל.');
                onClose();
              } catch (e) {
                toast.error(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            {t.agent.backfillStart}
          </Button>
          <Button onClick={onClose}>{t.common.cancel}</Button>
        </>
      }
    >
      <div className="stack">
        <p>{t.agent.backfillBody}</p>
        <div className="form-grid">
          <Field label="טווח">
            <Select value={preset} onChange={(e) => setMonths(e.target.value)}>
              <option value="3">3 חודשים אחרונים</option>
              <option value="6">6 חודשים אחרונים</option>
              <option value="12">שנה אחרונה</option>
              <option value="24">שנתיים אחרונות</option>
              <option value="custom">מתאריך מסוים</option>
            </Select>
          </Field>
          <Field label={t.agent.backfillSince}>
            <Input type="date" value={since} onChange={(e) => { setSince(e.target.value); setPreset('custom'); }} />
          </Field>
        </div>
        <Callout tone="info">מסמכים של לקוחות שעדיין לא קיימים במערכת ימתינו בתור הבדיקה. מומלץ לייבא קודם את רשימת הלקוחות.</Callout>
      </div>
    </Dialog>
  );
}
